import {
    Button,
    Typography,
    Modal,
    Box,
    Icon,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material'
import { AccessRestrictions, IGroupformData } from './interfaces'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { fetchGroupDataById, updateGroup } from '../../services/groupsServices'
import { CloseOutlined } from '@mui/icons-material'
import theme from '../../theme/theme'
import { useTranslation } from 'react-i18next'
import TruncatedText from '../Common/TruncatedText'
import {
    getApplicationSettings,
    ISettingsResponse,
} from '../../services/applications'
import { InTransitSettingsType } from '../Applications/Intransit/interfaces'
import { Applications, AttributeType } from '../../constants/constants'
import { fetchAttributes } from '../../services/dtServices'
import { fetchSubTemplates } from '../../services/entityInstanceServices'
import { flattenEntityTemplates } from '../../utils/EntityUtils'
import { useAppDispatch, useAppSelector } from '../../redux/hook'
import { updateLoader } from '../../redux/slices/loaderSlice'
import { defaultFormData } from './constants'
import UserGroupRecordsSelect from './UserGroupRecordsSelect'
import CheckboxInput from '../Common/CheckboxInput'
import PaperModalComponent from '../Common/PaperModalComponent'
import { isHidden } from '../../utils/AttributeUtils'
interface AccessControlProps {
    userGroupId: string
    handleClose: () => void
    readOnly: boolean
}
const AccessControl = (props: AccessControlProps): ReactElement => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const org = useAppSelector((state) => state.org.org)
    const { userGroupId, handleClose, readOnly } = props

    const [formValues, setFormValues] =
        useState<IGroupformData>(defaultFormData)

    const [initialFormValues, setInitialFormValues] =
        useState<IGroupformData>(defaultFormData)

    const [shipementSubTypesList, setShipementSubTypesList] = useState<any[]>(
        [],
    )
    const [shipementAttributeList, setShipementAttributeList] = useState<any[]>(
        [],
    )

    useEffect(() => {
        const getAccessControlData = async () => {
            try {
                dispatch(updateLoader(true))
                //fetch the group data
                const data = await fetchGroupDataById(
                    userGroupId,
                    org?.id || '',
                )
                let shipmentTemplateId =
                    data?.accessRestrictions?.templateId || ''
                const shipmentAttributeId = data?.accessRestrictions?.attributes
                    ?.length
                    ? data?.accessRestrictions?.attributes[0]?.attributeId || ''
                    : ''
                //set the list for shipment entity template and sub templates
                const inTransitAppSettingsResponse: ISettingsResponse<InTransitSettingsType> =
                    await getApplicationSettings<InTransitSettingsType>(
                        org?.id || '',
                        Applications.inTransit.id,
                    )
                const inTransitSipmentEntityTemplateId =
                    inTransitAppSettingsResponse?.appSettings
                        ?.shipmentEntityTemplateId || ''

                const getSubTemplates: any = await fetchSubTemplates(
                    org?.id || '',
                    inTransitSipmentEntityTemplateId,
                )

                let updatedTotalSubTemplates: any = []
                if (getSubTemplates?.length) {
                    updatedTotalSubTemplates =
                        flattenEntityTemplates(getSubTemplates)
                    updatedTotalSubTemplates = updatedTotalSubTemplates.filter(
                        (i: any) => !i.isAbstract,
                    )
                }

                //set the default value for shipment entity template
                if (!shipmentTemplateId) {
                    shipmentTemplateId =
                        updatedTotalSubTemplates[0]?.templateId || ''
                }

                //set the list for shipment attributes

                let shipmentAttributes = await fetchAttributes(
                    org?.id || '',
                    shipmentTemplateId,
                )
                shipmentAttributes = shipmentAttributes?.filter(
                    (i) =>
                        i.dataType.toLowerCase() === AttributeType.ENTITY &&
                        !isHidden(i),
                )

                //set the list for entity records (if shipment attribute is selected - edit mode)
                let selectedAttributeTemplateId = ''
                if (shipmentAttributeId) {
                    selectedAttributeTemplateId =
                        shipmentAttributes?.find(
                            (i) => i.id === shipmentAttributeId,
                        )?.range || ''
                }

                //set the values for the form
                const updatedShipmentAttributes = [
                    {
                        attributeTemplateId: selectedAttributeTemplateId,
                        attributeId: shipmentAttributeId,
                        records: data?.accessRestrictions?.attributes?.length
                            ? data?.accessRestrictions?.attributes[0]
                                  ?.records || []
                            : [],
                    },
                ]

                setShipementSubTypesList(updatedTotalSubTemplates)
                setShipementAttributeList(shipmentAttributes)
                setFormValues({
                    ...data,
                    accessRestrictions: {
                        templateId: shipmentTemplateId,
                        attributes: updatedShipmentAttributes,
                        includeEmptyRecords:
                            !!data?.accessRestrictions?.includeEmptyRecords,
                    },
                })
                setInitialFormValues({
                    ...data,
                    accessRestrictions: {
                        templateId: shipmentTemplateId,
                        attributes: updatedShipmentAttributes,
                        includeEmptyRecords:
                            !!data?.accessRestrictions?.includeEmptyRecords,
                    },
                })
            } catch (e) {
                console.log(e)
            } finally {
                dispatch(updateLoader(false))
            }
        }
        if (userGroupId && org?.id) {
            getAccessControlData()
        }
    }, [userGroupId, org?.id, dispatch])

    // shipemt entity template
    const onChangeShipmentSubTypes = useCallback(
        async (value: string) => {
            dispatch(updateLoader(true))

            const shipmentAttributes = await fetchAttributes(
                org?.id || '',
                value,
            )
            const shipmentAttributesList = shipmentAttributes?.filter(
                (i) => i.dataType.toLowerCase() === AttributeType.ENTITY,
            )
            setShipementAttributeList(shipmentAttributesList)

            setFormValues({
                ...formValues,
                accessRestrictions: {
                    templateId: value,
                    attributes: [
                        {
                            attributeId: '',
                            records: [],
                        },
                    ],
                    includeEmptyRecords: false,
                },
            })
            dispatch(updateLoader(false))
        },
        [org?.id, formValues, dispatch],
    )

    const onChangeShipmentAttribute = useCallback(
        (value: any) => {
            dispatch(updateLoader(true))

            const selectedAttributeTemplateId =
                shipementAttributeList?.find((i) => i.id === value)?.range || ''

            const updatedAccessRestrictions = {
                ...(formValues.accessRestrictions || {}),
                attributes: [
                    {
                        attributeTemplateId: selectedAttributeTemplateId,
                        attributeId: value,
                        records: [],
                    },
                ],
            }

            setFormValues({
                ...formValues,
                accessRestrictions: updatedAccessRestrictions,
            })
            dispatch(updateLoader(false))
        },
        [formValues, shipementAttributeList, dispatch],
    )

    const onChangeEntityRecords = useCallback(
        (value: any[]) => {
            const updatedAccessRestrictions = {
                ...formValues.accessRestrictions,
                attributes: [
                    {
                        ...(formValues.accessRestrictions?.attributes?.length
                            ? formValues.accessRestrictions?.attributes[0] || {}
                            : {}),
                        records: value,
                    },
                ],
            }
            setFormValues({
                ...formValues,
                accessRestrictions: updatedAccessRestrictions,
            })
        },
        [formValues],
    )

    const onChangeEmptyRecords = useCallback(
        (value: boolean) => {
            const updatedAccessRestrictions = {
                ...formValues.accessRestrictions,
                includeEmptyRecords: value,
            }
            setFormValues({
                ...formValues,
                accessRestrictions:
                    updatedAccessRestrictions as unknown as AccessRestrictions,
            })
        },
        [formValues],
    )

    const clearForm = () => {
        setFormValues(defaultFormData)
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        await updateGroup(formValues, org?.id || '')
        handleClose()
        clearForm()
    }

    const isSubmitDisabled = useMemo(() => {
        return (
            JSON.stringify(formValues.accessRestrictions) ===
            JSON.stringify(initialFormValues.accessRestrictions)
        )
    }, [formValues, initialFormValues])

    const selectedAttributeName = useMemo(() => {
        const selectedAttributeId = formValues?.accessRestrictions?.attributes
            ?.length
            ? formValues?.accessRestrictions?.attributes[0]?.attributeId || ''
            : ''
        if (selectedAttributeId) {
            return (
                shipementAttributeList?.find(
                    (i) => i.id === selectedAttributeId,
                )?.name || ''
            )
        } else {
            return ''
        }
    }, [formValues, shipementAttributeList])

    return (
        <div>
            <Modal
                open={true}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <PaperModalComponent>
                    <form onSubmit={handleSubmit}>
                        <Box
                            sx={{
                                p: 4,
                                boxShadow: 4,
                                borderRadius: 4,
                                position: 'absolute' as const,
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 400,
                                backgroundColor: '#fff',
                                border: `2px solid ${theme.palette.primary.main}`,
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '15px',
                                    cursor: 'pointer',
                                }}
                            >
                                <Icon
                                    onClick={() => handleClose()}
                                    sx={{ marginRight: '8px' }}
                                    fontSize="small"
                                    id="add-group-close-icon"
                                >
                                    <CloseOutlined
                                        sx={{
                                            color: '#000',
                                            marginTop: '-2px',
                                        }}
                                    />
                                </Icon>
                            </Box>
                            <Typography
                                variant="h6"
                                gutterBottom
                                component="h2"
                                id="draggable-modal-title"
                                sx={{ cursor: 'move' }}
                            >
                                {t('Access Control')}
                            </Typography>
                            <Box sx={{ mt: '20px' }}>
                                <FormControl fullWidth disabled={readOnly}>
                                    <InputLabel id="shipment-template-select-label">
                                        {t('Select Entity')}
                                    </InputLabel>
                                    <Select
                                        labelId="shipment-template-select-label"
                                        id="shipment-template-select"
                                        value={
                                            formValues?.accessRestrictions
                                                ?.templateId || ''
                                        }
                                        label={t('Select Entity')}
                                        onChange={(e) =>
                                            onChangeShipmentSubTypes(
                                                e.target.value,
                                            )
                                        }
                                        sx={{ fontSize: '14px' }}
                                        MenuProps={{
                                            anchorOrigin: {
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            },
                                            transformOrigin: {
                                                vertical: 'top',
                                                horizontal: 'left',
                                            },
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 280,
                                                },
                                                sx: {
                                                    width: 'auto',
                                                    minWidth: '100%',
                                                },
                                            },
                                        }}
                                    >
                                        {shipementSubTypesList?.map((o) => (
                                            <MenuItem
                                                sx={{
                                                    fontSize: '14px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                                key={o.templateId}
                                                id={o.templateId}
                                                value={o.templateId}
                                            >
                                                <TruncatedText
                                                    text={t(o.templateName)}
                                                    sx={{
                                                        fontSize: '14px',
                                                    }}
                                                />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box sx={{ mt: '20px' }}>
                                <FormControl fullWidth disabled={readOnly}>
                                    <InputLabel id="shipment-attribute-select-label">
                                        {t('Select Attribute')}
                                    </InputLabel>
                                    <Select
                                        labelId="shipment-attribute-select-label"
                                        id="shipment-attribute-select"
                                        value={
                                            formValues?.accessRestrictions
                                                ?.attributes?.length
                                                ? formValues?.accessRestrictions
                                                      ?.attributes[0]
                                                      ?.attributeId || ''
                                                : ''
                                        }
                                        label={t('Select Attribute')}
                                        onChange={(e) =>
                                            onChangeShipmentAttribute(
                                                e.target.value,
                                            )
                                        }
                                        // disabled={mode === ORG_ADD_MODE.EDIT}
                                        sx={{ fontSize: '14px' }}
                                        MenuProps={{
                                            anchorOrigin: {
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            },
                                            transformOrigin: {
                                                vertical: 'top',
                                                horizontal: 'left',
                                            },
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 280,
                                                },
                                                sx: {
                                                    width: 'auto',
                                                    minWidth: '100%',
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem
                                            sx={{
                                                fontSize: '14px',
                                                opacity: '0.4',
                                            }}
                                            value=""
                                            id={'attribute-empty-option'}
                                        >
                                            {t('selectPlaceholder')}
                                        </MenuItem>
                                        {shipementAttributeList?.map((o) => (
                                            <MenuItem
                                                sx={{
                                                    fontSize: '14px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                                key={o.id}
                                                id={o.id}
                                                value={o.id}
                                            >
                                                <TruncatedText
                                                    text={t(o.name)}
                                                    sx={{
                                                        fontSize: '14px',
                                                    }}
                                                />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box sx={{ mt: '20px' }}>
                                <UserGroupRecordsSelect
                                    label={`${t(selectedAttributeName)}`}
                                    id="entity-records-select"
                                    value={
                                        formValues?.accessRestrictions
                                            ?.attributes?.length
                                            ? formValues?.accessRestrictions
                                                  ?.attributes[0]?.records
                                            : []
                                    }
                                    onChange={(
                                        id: string,
                                        selectedList: any[],
                                    ) => onChangeEntityRecords(selectedList)}
                                    disabled={
                                        readOnly ||
                                        (formValues?.accessRestrictions
                                            ?.attributes?.length
                                            ? !formValues?.accessRestrictions
                                                  ?.attributes[0]?.attributeId
                                            : true)
                                    }
                                    templateId={
                                        formValues?.accessRestrictions
                                            ?.attributes?.length
                                            ? formValues?.accessRestrictions
                                                  ?.attributes[0]
                                                  ?.attributeTemplateId || ''
                                            : ''
                                    }
                                    orgId={org?.id || ''}
                                />
                            </Box>
                            <Box sx={{ mt: '20px' }}>
                                <CheckboxInput
                                    id="empty-records-checkbox"
                                    label={t('Empty Records')}
                                    value={
                                        !!formValues?.accessRestrictions
                                            ?.includeEmptyRecords
                                    }
                                    onChange={(id: string, value: boolean) =>
                                        onChangeEmptyRecords(value)
                                    }
                                    disabled={readOnly}
                                />
                            </Box>
                            {readOnly ? null : (
                                <Button
                                    sx={{
                                        mt: '20px',
                                    }}
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    id="add-group-submit-button"
                                    disabled={isSubmitDisabled}
                                    type="submit"
                                >
                                    {t('submit')}
                                </Button>
                            )}
                        </Box>
                    </form>
                </PaperModalComponent>
            </Modal>
        </div>
    )
}
export default AccessControl
